import {bookmarkDays} from './api-data';
import {element, icon} from './remote-data';
import {currentLocale, translate} from './preferences';
import {reconcileChildren, pruneEntries, setText} from './bookmark-render';
import {bookmarkCard, bookmarkDateLabel} from './bookmark-card';

export default function bookmarkArchive(section, signal) {
    const viewport = section.querySelector('[data-bookmark-viewport]');
    const groups = section.querySelector('[data-bookmark-groups]');
    const desktop = window.matchMedia('(min-width: 901px)');
    const monthsByDate = new Map(), daysByDate = new Map(), cardsById = new Map();
    let pendingHash = location.hash.slice(1);
    const top = node => node.getBoundingClientRect().top - viewport.getBoundingClientRect().top + viewport.scrollTop;
    function scrollToDate(group, behavior = 'smooth') {
        if (desktop.matches) viewport.scrollTo({top: top(group), behavior});
        else group.scrollIntoView({block: 'start', behavior});
    }
    window.addEventListener('hashchange', () => {
        pendingHash = location.hash.slice(1);
        const group = document.getElementById(pendingHash);
        if (group && groups.contains(group)) { scrollToDate(group); pendingHash = ''; }
    }, {signal});
    return {
        render(items) {
            const locale = currentLocale(), zh = locale === 'zh-TW';
            const scroll = viewport.scrollTop, pageScroll = window.scrollY;
            const focused = document.activeElement?.dataset.bookmarkId;
            const days = bookmarkDays(items);
            const countLabel = count => zh ? `${count} 個書籤` : `${count} bookmark${count === 1 ? '' : 's'}`;
            const months = new Map();
            for (const {day, items: saved} of days) {
                const month = day.slice(0, 7);
                let monthState = months.get(month);
                if (!monthState) {
                    let node = monthsByDate.get(month);
                    if (!node) {
                        node = element('div', 'bookmark-archive-month');
                        node.dataset.savedMonth = month;
                        const heading = element('div', 'bookmark-month-heading');
                        const title = element('h2', 'bookmark-month-title');
                        title.append(element('span'), element('span', 'bookmark-month-year', day.slice(0, 4)));
                        heading.append(title, element('p', 'bookmark-month-count'));
                        node.append(heading, element('div', 'bookmark-month-days'));
                        monthsByDate.set(month, node);
                    }
                    setText(node.querySelector('.bookmark-month-title span'), bookmarkDateLabel(day, 'month'));
                    monthState = {node, days: [], count: 0};
                    months.set(month, monthState);
                }
                monthState.count += saved.length;
                let group = daysByDate.get(day);
                if (!group) {
                    group = element('section', 'bookmark-archive-day');
                    group.id = `saved-${day}`; group.dataset.savedDate = day;
                    group.setAttribute('aria-labelledby', `saved-heading-${day}`);
                    const marker = icon(section.dataset.dateIcon, 16); marker.classList.add('bookmark-spine-marker'); marker.height = 28;
                    const dot = icon(section.dataset.dotIcon, 8); dot.classList.add('bookmark-spine-dot');
                    const heading = element('div', 'bookmark-group-heading');
                    const title = element('h2'); title.id = `saved-heading-${day}`; title.tabIndex = -1;
                    const time = element('time'); time.dateTime = day;
                    title.append(time);
                    const count = element('p', 'bookmark-group-count');
                    count.append(element('span', 'bookmark-count-desktop'), element('span', 'bookmark-count-mobile'));
                    heading.append(title, count);
                    group.append(marker, dot, heading, element('div', 'bookmark-group-cards'));
                    daysByDate.set(day, group);
                }
                const time = group.querySelector('time');
                setText(time, bookmarkDateLabel(day));
                time.setAttribute('aria-label', bookmarkDateLabel(day, 'long'));
                setText(group.querySelector('.bookmark-count-desktop'), countLabel(saved.length));
                setText(group.querySelector('.bookmark-count-mobile'), zh ? `${saved.length} 筆收藏` : `${saved.length} saved`);
                const cards = saved.map(item => {
                    const card = bookmarkCard(item, day, section, true, cardsById.get(item.id));
                    cardsById.set(item.id, card);
                    return card;
                });
                reconcileChildren(group.lastElementChild, cards);
                monthState.days.push(group);
            }
            const content = [];
            for (const {node, days: monthDays, count} of months.values()) {
                setText(node.querySelector('.bookmark-month-count'), countLabel(count));
                reconcileChildren(node.lastElementChild, monthDays);
                content.push(node);
            }
            reconcileChildren(groups, content);
            pruneEntries(monthsByDate, new Set(months.keys()));
            pruneEntries(daysByDate, new Set(days.map(({day}) => day)));
            pruneEntries(cardsById, new Set(items.map(item => item.id)));
            groups.hidden = !items.length;
            viewport.setAttribute('aria-label', translate('bookmarkArchiveLabel'));
            groups.lang = locale;
            viewport.scrollTop = scroll;
            if (section.isConnected && !desktop.matches) window.scrollTo({top: pageScroll, behavior: 'instant'});
            if (focused) [...groups.querySelectorAll('.bookmark-card')].find(card => card.dataset.bookmarkId === focused)?.focus({preventScroll: true});
            const target = section.isConnected && pendingHash && document.getElementById(pendingHash);
            if (target && groups.contains(target)) { scrollToDate(target, 'instant'); pendingHash = ''; }
        }
    };
}
